import { zodResolver } from "@hookform/resolvers/zod";
import { Box, Grid, makeStyles, Typography } from "@material-ui/core";
import dayjs from "dayjs";
import * as React from "react";
import { useForm, Controller } from "react-hook-form";
import { useRecoilValue } from "recoil";

import { CustomButton } from "@/components/Button";
import { PropertyEditErrorSummary } from "@/components/property_edit/PropertyEditErrorSummary";
import { CustomForm } from "@/components/property_edit/PropertyEditInputs";
import theme from "@/theme";
import { parseFormattedNumber } from "@/utilities/format";
import { loanAmountPeriodSchema, LoanAmountPeriodFormData } from "@/validations/management/edit";
import { SelectedProperty } from "@/view_models/propertySelectors";

import { usePatchProperty } from "../../../hooks/usePatchProperty";
import { LoanConditionsDraft, purchaseLoanHistory, hasApplicableLoanRate } from "../../../utils/purchaseLoanDraft";
import { EditPropertyHistoryItem } from "../../EditPropertyHistoryItem";

const useStyles = makeStyles(() => ({
  background: {
    backgroundColor: theme.palette.background.default,
    borderRadius: 8,
    padding: 16,
  },
}));

interface Props {
  initiallyEditing?: boolean;
  pendingBoughtAt?: string;
  draft?: LoanConditionsDraft;
  onDraft?: (draft: LoanConditionsDraft) => void;
  onSaved?: () => void;
  onCancel?: () => void;
}

export const LoanAmountPeriodEditor: React.FC<Props> = ({
  initiallyEditing = false,
  onSaved,
  onCancel,
  pendingBoughtAt,
  draft,
  onDraft,
}) => {
  const classes = useStyles();
  const [isEditing, setIsEditing] = React.useState(initiallyEditing);
  const { patchProperty, isLoading, error, clearError } = usePatchProperty(true);

  // 選択済み物件取得
  const selected_property = useRecoilValue(SelectedProperty);

  const purchaseMonth = dayjs(selected_property.property.bought_at).startOf("month");
  const minimumRepaymentMonth = pendingBoughtAt ? dayjs(pendingBoughtAt).startOf("month") : purchaseMonth;

  const savedValues = React.useRef<{ form: LoanAmountPeriodFormData; paymentStartsAt: string }>();
  const savedInitialMonth = React.useRef<string>();
  React.useEffect(() => {
    savedValues.current = undefined;
    savedInitialMonth.current = undefined;
  }, [selected_property]);
  const currentValues = (): LoanAmountPeriodFormData => ({
    payment_starts_at: dayjs(draft?.payment_starts_at || selected_property.property.payment_starts_at).format(
      "YYYY-MM"
    ),
    loan_amount: (draft?.loan_amount ?? selected_property.property.loan_amount).toString(),
    payment_period: (draft?.payment_period ?? selected_property.property.payment_period).toString(),
    initial_loan_interest_rate_percentage: draft?.initial_loan_interest_rate_percentage?.toString() ?? "",
  });

  const {
    control,
    reset,
    watch,
    formState: { errors },
    handleSubmit,
    setError,
  } = useForm<LoanAmountPeriodFormData>({
    resolver: zodResolver(loanAmountPeriodSchema),
    defaultValues: currentValues(),
  });

  const watchedStart = watch("payment_starts_at");
  const watchedAmount = watch("loan_amount");
  const history = pendingBoughtAt
    ? purchaseLoanHistory(
        selected_property.user_input_monthly_cashflow_results,
        selected_property.property.bought_at,
        pendingBoughtAt
      )
    : selected_property.user_input_monthly_cashflow_results;
  const needsInitialRate =
    parseFormattedNumber(watchedAmount || "0") > 0 &&
    !(savedInitialMonth.current && !dayjs(savedInitialMonth.current).isAfter(dayjs(watchedStart), "month")) &&
    !hasApplicableLoanRate(history, watchedStart);

  const onSubmit = async (data: LoanAmountPeriodFormData) => {
    if (parseFormattedNumber(data.loan_amount) > 0 && dayjs(data.payment_starts_at).isBefore(minimumRepaymentMonth)) {
      setError("payment_starts_at", {
        message: `初回返済月は${minimumRepaymentMonth.format("YYYY/MM")}以降にしてください。`,
      });
      return;
    }
    if (needsInitialRate && !data.initial_loan_interest_rate_percentage?.trim()) {
      setError("initial_loan_interest_rate_percentage", { message: "初回返済月の年利を入力してください。" });
      return;
    }
    const previousPaymentDate =
      draft?.payment_starts_at || savedValues.current?.paymentStartsAt || selected_property.property.payment_starts_at;
    const sendData = {
      ...(needsInitialRate
        ? { initial_loan_interest_rate_percentage: Number(data.initial_loan_interest_rate_percentage) }
        : {}),
      // 年月を変えていない場合は既存の日付を保つ。
      payment_starts_at:
        data.payment_starts_at === dayjs(previousPaymentDate).format("YYYY-MM")
          ? previousPaymentDate
          : `${data.payment_starts_at}-01`,
      payment_period: parseInt(data.payment_period),
      loan_amount: parseFormattedNumber(data.loan_amount),
    };

    if (onDraft) {
      onDraft(sendData);
      return;
    }

    if (await patchProperty(sendData)) {
      savedValues.current = {
        form: { ...data, initial_loan_interest_rate_percentage: "" },
        paymentStartsAt: sendData.payment_starts_at,
      };
      if (needsInitialRate) savedInitialMonth.current = data.payment_starts_at;
      setIsEditing(false);
      onSaved?.();
    }
  };

  const handleEdit = () => {
    // 再取得前に開き直した場合も直前の保存値を使い、取得後は最新の物件値を使う。
    reset(savedValues.current?.form || currentValues());
    clearError();
    setIsEditing(true);
  };

  const handleCancel = () => {
    clearError();
    setIsEditing(false);
    onCancel?.();
  };

  return (
    <Box className={classes.background} data-edit-form-panel>
      {isEditing ? (
        <form onSubmit={handleSubmit(onSubmit)}>
          {pendingBoughtAt && (
            <Typography variant="body2">
              変更予定の購入月は{dayjs(pendingBoughtAt).format("YYYY/MM")}
              です。ここでは保存せず、購入時情報の「変更を保存」で一緒に保存します。
            </Typography>
          )}
          <Box mb={2}>
            <Controller
              name="loan_amount"
              control={control}
              render={({ ...field }) => (
                <CustomForm
                  {...field}
                  label="ローン借入金額"
                  required
                  error={!!errors.loan_amount}
                  errorText={errors.loan_amount?.message}
                  placeHolder="100,000,000"
                  unitSuffix="円"
                  formatNumeric
                />
              )}
            />
          </Box>
          <Box mb={2}>
            <Controller
              name="payment_period"
              control={control}
              render={({ ...field }) => (
                <CustomForm
                  {...field}
                  label="返済年数"
                  required
                  error={!!errors.payment_period}
                  errorText={errors.payment_period?.message}
                  placeHolder="35"
                  unitSuffix="年"
                />
              )}
            />
          </Box>
          <Box mb={2}>
            <Controller
              name="payment_starts_at"
              control={control}
              render={(field) => (
                <CustomForm
                  {...field}
                  label="初回返済年月"
                  type="month"
                  required
                  error={!!errors.payment_starts_at}
                  errorText={errors.payment_starts_at?.message}
                />
              )}
            />
            <Typography variant="body2">
              購入月は{minimumRepaymentMonth.format("YYYY/MM")}です。
              初回返済年月を訂正すると、過去の残債・返済額も再計算されます。金利の適用開始年月とは別の項目です。
            </Typography>
          </Box>
          {needsInitialRate && (
            <Box mb={2}>
              <Typography variant="body2">
                この初回返済月に適用できる金利がありません。初期金利を入力すると、借入条件と一緒に登録します。後の金利履歴は残ります。
              </Typography>
              <Controller
                name="initial_loan_interest_rate_percentage"
                control={control}
                defaultValue=""
                render={(field) => (
                  <CustomForm
                    {...field}
                    label="初回返済月の年利"
                    required
                    unitSuffix="%"
                    disabled={isLoading}
                    error={!!errors.initial_loan_interest_rate_percentage}
                    errorText={errors.initial_loan_interest_rate_percentage?.message}
                  />
                )}
              />
            </Box>
          )}
          {error && (
            <Typography role="alert" color="error">
              {error}
            </Typography>
          )}
          <PropertyEditErrorSummary errors={errors} />
          <Grid container spacing={2} data-edit-form-actions>
            <Grid item xs={6}>
              <CustomButton
                customVariant="textPrimary"
                onClick={handleCancel}
                disabled={isLoading}
                size="small"
                fullWidth
              >
                キャンセル
              </CustomButton>
            </Grid>
            <Grid item xs={6}>
              <CustomButton
                buttonSize="L"
                customVariant="filled"
                type="submit"
                disabled={isLoading}
                size="small"
                fullWidth
              >
                {onDraft ? "入力内容を反映" : "保存"}
              </CustomButton>
            </Grid>
          </Grid>
        </form>
      ) : (
        <EditPropertyHistoryItem
          preserveLabels
          contents={[
            { label: "初回返済年月", value: dayjs(selected_property?.property.payment_starts_at).format("YYYY/MM") },
            {
              label: "ローン借入金額",
              value: selected_property?.property?.loan_amount
                ? `${selected_property.property.loan_amount.toLocaleString()}円`
                : "-円",
            },
            {
              label: "返済年数",
              value: selected_property?.property?.payment_period
                ? `${selected_property.property.payment_period}年`
                : "-年",
            },
          ]}
          onEdit={handleEdit}
        />
      )}
    </Box>
  );
};
